import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

type IssueSourceFormDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => Promise<void>;
  initialName?: string;
}>;

export function IssueSourceFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initialName,
}: IssueSourceFormDialogProps) {
  const isEdit = initialName != null;
  const [name, setName] = useState(initialName ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setName(initialName ?? "");
  }, [open, initialName]);

  async function handleSubmit() {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(name.trim());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  const title = isEdit ? "Edit source" : "Create source";
  const subtitle = isEdit
    ? "Update the source name."
    : "Add a new lead source.";
  const primaryLabel = isEdit ? "Save" : "Create";

  // One Dialog serves both breakpoints, and Enter submits the form.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        <Box
          as="form"
          paddingX="lg"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <Field>
            <FieldLabel>Name</FieldLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Source name"
              autoFocus
            />
          </Field>
        </Box>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim()}
            loading={submitting}
          >
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
